// WS-F 2026-07-21 — health views for the Dev Admin (operator).
//
// PlatformHealthPanel: whole-platform view — host/server metrics from the
// shared /settings/server-stats endpoint (host-global; operator authorized as
// platform-tenant admin) + CROSS-TENANT totals from /platform/health/platform
// (aggregated wall-safely tenant-by-tenant on the app role).
// TenantHealthPanel: ONE tenant's own numbers from /platform/tenants/:id/health
// (strictly RLS-scoped). Server/hardware/DB-size panels are deliberately
// absent here. Both are READ-ONLY dashboards.
import { useState, useEffect, useCallback } from "react";
import { T } from "../theme";
import { apiCall } from "../apiClient";
import { RefreshBtn } from "./primitives";

const Tile = ({ label, value, sub }: { label: string; value: any; sub?: string }) => (
  <div style={{ padding: "10px 12px", borderRadius: 8, border: `1px solid ${T.brd}`, backgroundColor: T.bg2, minWidth: 120 }}>
    <div style={{ fontSize: 10, color: T.txD, textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: T.tx, marginTop: 2 }}>{value ?? "—"}</div>
    {sub && <div style={{ fontSize: 10.5, color: T.txM }}>{sub}</div>}
  </div>
);
const Section = ({ title, children }: { title: string; children: any }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: T.txD, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 8 }}>{title}</div>
    {children}
  </div>
);
const Row = ({ children }: { children: any }) => (
  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>{children}</div>
);

export const PlatformHealthPanel = () => {
  const [stats, setStats] = useState<any>(null);
  const [agg, setAgg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    // ONE operator-gated fetch: server stats ride /platform/health/platform
    // (the old /settings/server-stats fetch 403'd for operators whose platform
    // tenant lacks the admin.view_platform_health permission row → blank panels).
    const a = await apiCall("/platform/health/platform");
    setStats(a?.serverStats || null); setAgg(a); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ fontSize: 13, color: T.txM }}>Loading platform health…</div>;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}><RefreshBtn onClick={load} /></div>
      <Section title="Server performance (dev platform host)">
        <Row>
          <Tile label="CPU" value={`${stats?.cpu?.usage_percent ?? "—"}%`} sub={`load ${stats?.cpu?.load_1m} · ${stats?.cpu?.cores} cores`} />
          <Tile label="Memory" value={`${stats?.memory?.usage_percent ?? "—"}%`} sub={`${stats?.memory?.used_mb}/${stats?.memory?.total_mb} MB`} />
          <Tile label="Swap" value={`${stats?.swap?.usage_percent ?? "—"}%`} sub={`${stats?.swap?.used_mb}/${stats?.swap?.total_mb} MB`} />
          <Tile label="Disk" value={stats?.disk?.percent} sub={`${stats?.disk?.used} / ${stats?.disk?.total}`} />
          <Tile label="Uptime" value={stats?.uptime?.formatted} />
          <Tile label="Node.js" value={`${stats?.process?.rss_mb ?? "—"} MB RSS`} sub={`${stats?.process?.node_version} · up ${stats?.process?.uptime_formatted}`} />
          <Tile label="PostgreSQL" value={stats?.postgres?.size} sub={`${stats?.postgres?.connections} connections`} />
        </Row>
      </Section>
      <Section title="Platform-wide totals (all tenants)">
        <Row>
          <Tile label="Tenants" value={agg?.totals?.tenants} />
          <Tile label="Users" value={agg?.totals?.users} sub={`${agg?.totals?.logins7d ?? 0} logins 7d`} />
          <Tile label="Requests" value={agg?.totals?.requests} />
          <Tile label="Contracts" value={agg?.totals?.contracts} />
          <Tile label="AI fields" value={agg?.totals?.aiFields} />
          <Tile label="Imports 30d" value={agg?.totals?.imports30d?.total} sub={`${agg?.totals?.imports30d?.success ?? 0} ok · ${agg?.totals?.imports30d?.failed ?? 0} failed`} />
          <Tile label="Extraction 30d" value={agg?.totals?.extraction30dContracts} sub="contracts extracted" />
          <Tile label="Inbound email 24h" value={agg?.totals?.inbound24h} />
        </Row>
      </Section>
      <Section title="Per-tenant snapshot">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead><tr>{["Tenant", "Status", "Users", "Requests", "Contracts"].map(h => <th key={h} style={{ textAlign: "left", padding: "6px 8px", color: T.txD, fontSize: 10.5, textTransform: "uppercase", borderBottom: `1px solid ${T.brd}` }}>{h}</th>)}</tr></thead>
          <tbody>
            {(agg?.tenants || []).map((t: any) => (
              <tr key={t.tenant_id}>
                <td style={{ padding: "6px 8px", color: T.tx }}>{t.name || t.tenant_id}{t.error && <span style={{ color: T.red, fontSize: 10 }}> ({t.error})</span>}</td>
                <td style={{ padding: "6px 8px", color: T.txM }}>{t.status}</td>
                <td style={{ padding: "6px 8px", color: T.txM }}>{t.users ?? "—"}</td>
                <td style={{ padding: "6px 8px", color: T.txM }}>{t.requests ?? "—"}</td>
                <td style={{ padding: "6px 8px", color: T.txM }}>{t.contracts ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
};

export const TenantHealthPanel = ({ tenantId }: { tenantId: string }) => {
  const [h, setH] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    setH(await apiCall(`/platform/tenants/${tenantId}/health`));
    setLoading(false);
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ fontSize: 13, color: T.txM }}>Loading tenant health…</div>;
  if (!h) return <div style={{ fontSize: 13, color: T.red }}>Could not load tenant health.</div>;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: T.txM }}>All numbers below are <strong>this tenant's own</strong> (RLS-scoped). Host/server metrics live in the platform view.</span>
        <RefreshBtn onClick={load} />
      </div>
      <Section title="Users">
        <Row>
          <Tile label="Total users" value={h.users?.total} sub={`${h.users?.logins7d ?? 0} logins 7d · ${h.users?.logins30d ?? 0} 30d`} />
          {(h.users?.byRole || []).map((r: any) => <Tile key={r.role} label={r.role} value={r.count} />)}
        </Row>
      </Section>
      <Section title="Data volume">
        <Row>
          <Tile label="Requests" value={h.data?.requests} />
          <Tile label="Contracts" value={h.data?.contracts} />
          <Tile label="AI fields" value={h.data?.aiFields} />
          <Tile label="Activity log" value={h.data?.activityLogs} />
          <Tile label="Unread notifications" value={h.data?.unreadNotifications} />
        </Row>
      </Section>
      <Section title="Import health (30 days)">
        <Row>
          <Tile label="Batches" value={h.imports30d?.total} />
          <Tile label="Succeeded" value={h.imports30d?.success} />
          <Tile label="Failed" value={h.imports30d?.failed} />
        </Row>
      </Section>
      <Section title="AI extraction">
        <Row>
          <Tile label="Extracted fields" value={h.extraction?.total} />
          <Tile label="Contracts 30d" value={h.extraction?.recentContracts30d} />
          {(h.extraction?.byConfidence || []).map((b: any) => <Tile key={b.bucket} label={`${b.bucket} confidence`} value={b.count} sub={`avg ${b.avg_score}`} />)}
        </Row>
        {(h.extraction?.byContractType || []).length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, marginTop: 8 }}>
            <thead><tr>{["Contract type", "Contracts", "Fields"].map(x => <th key={x} style={{ textAlign: "left", padding: "5px 8px", color: T.txD, fontSize: 10.5, textTransform: "uppercase", borderBottom: `1px solid ${T.brd}` }}>{x}</th>)}</tr></thead>
            <tbody>{h.extraction.byContractType.map((r: any) => (
              <tr key={r.contract_type}><td style={{ padding: "5px 8px", color: T.tx }}>{r.contract_type}</td><td style={{ padding: "5px 8px", color: T.txM }}>{r.contracts}</td><td style={{ padding: "5px 8px", color: T.txM }}>{r.fields}</td></tr>
            ))}</tbody>
          </table>
        )}
      </Section>
      <Section title="Inbound email / reviewer activity (24h)">
        <Row>
          <Tile label="Reviewer invites" value={h.inboundEmail?.reviewerInvites24h} />
          <Tile label="Feedback received" value={h.inboundEmail?.reviewerFeedback24h} />
          {(h.inboundEmail?.ledger24h || []).map((r: any) => <Tile key={r.outcome} label={`email: ${r.outcome}`} value={r.count} />)}
        </Row>
      </Section>
      <Section title="Integrations">
        <Row>
          <Tile label="SharePoint sync" value={h.integrations?.sharepoint_enabled ? "Enabled" : "Off"} />
        </Row>
      </Section>
    </div>
  );
};
